import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

// One owner for the shared dialog, across all cards. No product HTML cache/prefetch.
let pendingPreview = null;
function cancelPendingPreview() {
  const pending = pendingPreview;
  pendingPreview = null;
  pending?.controller.abort();
}
function desktopPreview() { return !isMobileBreakpoint(); }
function dialogElement() { return document.getElementById('quick-add-dialog'); }
mediaQueryLarge.addEventListener('change', () => {
  cancelPendingPreview();
  const dialog = dialogElement();
  if (dialog instanceof QuickAddDialog) dialog.closeDialog();
});

export class QuickAddComponent extends Component {
  #lifecycle = new AbortController();
  #scrollFrame;
  #busyButton = null;
  #busyRequest = null;
  #buttonWasDisabled = false;

  get productPageUrl() {
    const card = this.closest('product-card');
    if (card) return card.productPageUrl;
    return this.closest('product-hotspot-component')?.getHotspotProductLink()?.href || '';
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#lifecycle.signal.aborted) this.#lifecycle = new AbortController();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (pendingPreview?.owner === this) cancelPendingPreview();
    this.#lifecycle.abort();
    if (this.#scrollFrame !== undefined) cancelAnimationFrame(this.#scrollFrame);
    this.#clearBusy();
    this.removeAttribute('stay-visible');
  }

  #clearBusy() {
    if (!this.#busyButton) return;
    this.#busyButton.removeAttribute('aria-busy');
    this.#busyButton.disabled = this.#buttonWasDisabled;
    this.#busyButton = null;
    this.#busyRequest = null;
  }

  handleClick = async (event) => {
    event.preventDefault();
    cancelPendingPreview();
    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;
    let parsedUrl;
    try { parsedUrl = new URL(currentUrl, window.location.href); } catch { return; }
    if (!['https:','http:'].includes(parsedUrl.protocol)) return;
    if (!desktopPreview() || this.dataset.usesSellingPlans === 'true' || parsedUrl.origin !== window.location.origin) {
      window.location.assign(parsedUrl.href);
      return;
    }
    this.#clearBusy();
    const pending = {owner:this,controller:new AbortController()};
    pendingPreview = pending;
    const button = this.querySelector('.quick-add__button--choose');
    if (button instanceof HTMLButtonElement) {
      this.#busyButton = button;
      this.#busyRequest = pending;
      this.#buttonWasDisabled = button.disabled;
      button.setAttribute('aria-busy','true');
      button.disabled = true;
    }
    const isCurrent = () => pendingPreview === pending && this.isConnected && desktopPreview();
    const timeout = setTimeout(() => pending.controller.abort(), 12000);
    try {
      const html = await this.fetchProductPage(parsedUrl.href, pending.controller.signal);
      if (!isCurrent()) return;
      const grid = html?.querySelector('[data-product-grid-content]');
      const dialog = dialogElement();
      if (!grid || !(dialog instanceof QuickAddDialog)) throw new Error('Preview unavailable');
      await this.updateQuickAddModal(grid.cloneNode(true));
      if (!isCurrent()) return;
      this.toggleAttribute('stay-visible',true);
      dialog.addEventListener(DialogCloseEvent.eventName, () => this.removeAttribute('stay-visible'), {once:true,signal:this.#lifecycle.signal});
      dialog.refs?.dialog?.setAttribute('aria-label','Швидкий перегляд: ' + (this.dataset.productTitle || 'товар'));
      dialog.showDialog();
      this.#scrollFrame = requestAnimationFrame(() => {
        this.#scrollFrame = undefined;
        if (!this.isConnected || !dialog.refs?.dialog?.open) return;
        dialog.querySelector('.product-details')?.scrollTo({top:0,behavior:'auto'});
        dialog.querySelector('.product-information__media')?.scrollTo({top:0,behavior:'auto'});
      });
    } catch {
      // No write is retried. A failed owned read falls back to the real product page.
      if (isCurrent()) window.location.assign(parsedUrl.href);
    } finally {
      clearTimeout(timeout);
      if (pendingPreview === pending) pendingPreview = null;
      if (this.#busyRequest === pending) this.#clearBusy();
    }
  };

  async fetchProductPage(productPageUrl, signal) {
    if (!productPageUrl) return null;
    const response = await fetch(productPageUrl,{signal});
    if (!response.ok) throw new Error('Product preview HTTP ' + response.status);
    return new DOMParser().parseFromString(await response.text(),'text/html');
  }

  async updateQuickAddModal(productGrid) {
    const content = document.getElementById('quick-add-modal-content');
    if (!productGrid || !content || !productGrid.querySelector('.product-details')) throw new Error('Product layout unavailable');
    content.setAttribute('view-event-payload',productGrid.getAttribute('view-event-payload') || '');
    const variantSource = productGrid.cloneNode(true);
    morph(content,productGrid);
    const details = content.querySelector('.product-details');
    if (!details) throw new Error('Product details unavailable');
    content.querySelector('.veyro-quick-view-extra')?.remove();
    const extra = document.createElement('div');
    extra.className = 'veyro-quick-view-extra';
    const description = (this.dataset.productDescription || '').trim();
    if (description) {
      const paragraph = document.createElement('p');
      paragraph.className = 'veyro-quick-view-description';
      paragraph.textContent = description;
      extra.appendChild(paragraph);
    }
    const link = document.createElement('a');
    link.className = 'veyro-quick-view-link';
    link.textContent = 'Переглянути більше';
    link.href = content.querySelector('.view-product-title a')?.href || this.productPageUrl;
    extra.appendChild(link);
    details.appendChild(extra);
    const picker = content.querySelector('variant-picker');
    // Preserve the native picker's server-derived variant mappings and availability.
    picker?.updateVariantPicker(variantSource);
    const selectedId = this.closest('product-card')?.getSelectedVariantId() ?? null;
    if (!selectedId) return;
    for (const input of content.querySelectorAll('input[type="radio"][data-variant-id]')) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change',{bubbles:true}));
        break;
      }
    }
  }
}
if (!customElements.get('quick-add-component')) customElements.define('quick-add-component',QuickAddComponent);

class QuickAddDialog extends DialogComponent {
  #lifecycle = new AbortController();
  #session = 0;
  #variantVersion = 0;
  #iosFrame;
  #iosGrid;
  #iosWidth;

  #clearIOSRepaint() {
    if (this.#iosFrame !== undefined) cancelAnimationFrame(this.#iosFrame);
    if (this.#iosGrid) this.#iosGrid.style.width = this.#iosWidth;
    this.#iosFrame = undefined; this.#iosGrid = undefined;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#lifecycle.signal.aborted) this.#lifecycle = new AbortController();
    const signal = this.#lifecycle.signal;
    this.addEventListener(StandardEvents.cartLinesUpdate,this.handleCartUpdate,{signal});
    this.addEventListener(StandardEvents.productSelect,this.#handleProductSelect,{signal});
    this.addEventListener(DialogCloseEvent.eventName,this.#handleDialogClose,{signal});
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#lifecycle.abort(); this.#session++;
    cancelPendingPreview();
    this.#clearIOSRepaint();
  }
  handleCartUpdate = (event) => {
    const session = this.#session;
    if (!event.promise) return;
    Promise.resolve(event.promise).then(result => {
      if (!this.isConnected || session !== this.#session || !result?.cart || result.detail?.didError || result.userErrors?.length) return;
      this.closeDialog();
    }).catch(() => {});
  };
  #handleProductSelect = (event) => {
    const session = this.#session;
    const version = ++this.#variantVersion;
    if (!event.promise) return;
    Promise.resolve(event.promise).then(result => {
      if (!this.isConnected || session !== this.#session || version !== this.#variantVersion || !this.refs?.dialog?.open) return;
      const anchor = result?.detail?.html?.querySelector('.view-product-title a');
      if (!anchor) return;
      for (const link of this.querySelectorAll('.view-product-title a,.product-header a,.veyro-quick-view-link')) link.href = anchor.href;
    }).catch(() => {});
  };
  #handleDialogClose = () => {
    this.#session++;
    cancelPendingPreview();
    this.#clearIOSRepaint();
    const ios = getIOSVersion();
    if (!ios || ios.major >= 17 || (ios.major === 16 && ios.minor >= 4)) return;
    // Keep the original Horizon iOS <16.4 grid repaint workaround.
    this.#iosFrame = requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (!grid) return;
      const width = grid.getBoundingClientRect().width;
      this.#iosGrid = grid; this.#iosWidth = grid.style.width;
      grid.style.width = `${width-1}px`;
      this.#iosFrame = requestAnimationFrame(() => this.#clearIOSRepaint());
    });
  };
}
if (!customElements.get('quick-add-dialog')) customElements.define('quick-add-dialog',QuickAddDialog);
